import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export type SchedulerViewMode = 'day' | 'week' | 'month' | 'overview';
export type SchedulerDisplayMode = 'both' | 'appts' | 'blocks';
export type SchedulerBlockDisplayStyle = 'overlay' | 'side-by-side';

export interface SchedulerPrintOptions {
  printOption1: boolean;
  printOption2: boolean;
  printOption3: boolean;
  printOption4: boolean;
  printOption5: boolean;
  printOption6: boolean;
  printOption7: boolean;
  printOption8: boolean;
  printOption9: boolean;
  printOption10: boolean;
  printOption11: boolean;
  printOption12: boolean;
  printOption13: boolean;
  printOption14: boolean;
  printOption15: boolean;
  printOption16: boolean;
  printOption17: boolean;
  printOption18: boolean;
  printOption19: boolean;
  printOption20: boolean;
  printOption21: boolean;
}

export interface SchedulerPreference {
  schedulerPreferenceId: number;
  name: string;
  isDefault: boolean;
  shared: boolean;
  ownedByCurrentUser: boolean;
  userId: number;
  viewMode: SchedulerViewMode;
  displayMode: SchedulerDisplayMode;
  showHours: number;
  startHour: number;
  slotMinutes: number;
  showWeekends: boolean;
  showCancelled: boolean;
  showNoShows: boolean;
  showNoResource: boolean;
  showInfoPopups: boolean;
  showBlockInfoPopups: boolean;
  privacyMode: boolean;
  blockDisplayStyle: SchedulerBlockDisplayStyle;
  scheduleResourceIds: number[];
  filterResourceIds: number[];
  facilityIds: number[];
  appointmentTypeIds: number[];
  overviewScheduledOpen: boolean;
  overviewContactAttemptedOpen: boolean;
  overviewConfirmedOpen: boolean;
  overviewCheckedInOpen: boolean;
  overviewCheckedBackOpen: boolean;
  overviewCheckedOutOpen: boolean;
  overviewCanceledOpen: boolean;
  overviewNoShowOpen: boolean;
  overviewWaitingListOpen: boolean;
  printOptions: SchedulerPrintOptions;
  yesterdayCheckDate: string;
  /** Legacy apptCountToggle/apptHoursToggle/apptQuoteToggle (users_filters_sched):
   *  drive the per-column totals shown under day/week/month headers. */
  apptCountToggle: boolean;
  apptHoursToggle: boolean;
  apptQuoteToggle: boolean;
}

export interface SchedulerPreferencePayload {
  name?: string;
  shared?: boolean;
  viewMode: SchedulerViewMode;
  displayMode: SchedulerDisplayMode;
  showHours: number;
  startHour: number;
  slotMinutes: number;
  showWeekends: boolean;
  showCancelled: boolean;
  showNoShows?: boolean;
  showNoResource: boolean;
  showInfoPopups: boolean;
  showBlockInfoPopups?: boolean;
  privacyMode: boolean;
  blockDisplayStyle: SchedulerBlockDisplayStyle;
  scheduleResourceIds: number[];
  filterResourceIds: number[];
  facilityIds: number[];
  appointmentTypeIds: number[];
  overviewScheduledOpen?: boolean;
  overviewContactAttemptedOpen?: boolean;
  overviewConfirmedOpen?: boolean;
  overviewCheckedInOpen?: boolean;
  overviewCheckedBackOpen?: boolean;
  overviewCheckedOutOpen?: boolean;
  overviewCanceledOpen?: boolean;
  overviewNoShowOpen?: boolean;
  overviewWaitingListOpen?: boolean;
  printOptions?: Partial<SchedulerPrintOptions>;
  apptCountToggle?: boolean;
  apptHoursToggle?: boolean;
  apptQuoteToggle?: boolean;
  /** Legacy yesterdayCheckDate (users_filters_sched): records the date the
   *  user last dismissed the "previous day" banner so we suppress it per
   *  user instead of relying on localStorage (P1-6). */
  yesterdayCheckDate?: string;
}

export type SchedulerPreferencePartialPayload = Partial<SchedulerPreferencePayload>;

function unwrap<T>(promise: Promise<{ data: ApiResponse<T> } | { data: T }>): Promise<T> {
  return promise.then((response) => {
    const payload = response.data as ApiResponse<T>;
    return payload.data ?? (response.data as T);
  });
}

export function getSchedulerPreference(): Promise<SchedulerPreference> {
  return unwrap(apiClient.get<ApiResponse<SchedulerPreference>>('/scheduler/preferences'));
}

export function updateSchedulerPreference(
  payload: SchedulerPreferencePayload | SchedulerPreferencePartialPayload,
): Promise<SchedulerPreference> {
  return unwrap(apiClient.put<ApiResponse<SchedulerPreference>>('/scheduler/preferences', payload));
}

export function listSchedulerPresets(): Promise<SchedulerPreference[]> {
  return unwrap(apiClient.get<ApiResponse<SchedulerPreference[]>>('/scheduler/presets'));
}

export function createSchedulerPreset(
  payload: SchedulerPreferencePayload,
): Promise<SchedulerPreference> {
  return unwrap(apiClient.post<ApiResponse<SchedulerPreference>>('/scheduler/presets', payload));
}

export function updateSchedulerPreset(
  id: number,
  payload: SchedulerPreferencePayload,
): Promise<SchedulerPreference> {
  return unwrap(apiClient.put<ApiResponse<SchedulerPreference>>(`/scheduler/presets/${id}`, payload));
}

export function deleteSchedulerPreset(id: number): Promise<void> {
  return apiClient.delete(`/scheduler/presets/${id}`).then(() => undefined);
}

export function applySchedulerPreset(id: number): Promise<SchedulerPreference> {
  return unwrap(apiClient.post<ApiResponse<SchedulerPreference>>(`/scheduler/presets/${id}/apply`));
}
